import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarRange, Users, BadgePercent, Clock, ArrowUpRight } from "lucide-react";
import { useSite } from "@/i18n/SiteContext";
import { api, euro } from "@/lib/api";
import { BookingWidget } from "@/components/tours/BookingWidget";
import { Reveal } from "@/components/site/Reveal";
import { pkgTr, tourTitle } from "@/components/site/PackageCard";

export default function PackageDetailPage() {
  const { slug } = useParams();
  const { t, lang } = useSite();
  const [pkg, setPkg] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    api.get(`/packages/${slug}`).then((r) => { setPkg(r.data); setState("ok"); }).catch(() => setState("error"));
    window.scrollTo(0, 0);
  }, [slug]);

  if (state === "loading") {
    return <div className="pt-40 pb-32 text-center" data-testid="package-loading"><div className="w-10 h-10 mx-auto border-2 border-sea border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (state === "error") {
    return (
      <div className="pt-40 pb-32 px-6 text-center" data-testid="package-not-found">
        <p className="text-ocean/70">{t("pkg.notFound")}</p>
        <Link to="/packages" className="inline-block mt-6 px-6 py-3 bg-ocean text-ivory text-xs uppercase tracking-[0.2em] rounded-full">{t("pkg.back")}</Link>
      </div>
    );
  }

  const c = pkgTr(pkg, lang);

  return (
    <div data-testid="package-detail-page">
      <section className="relative h-[70vh] min-h-[520px] flex items-end overflow-hidden" data-testid="package-hero">
        <img src={pkg.image} alt={c.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative max-w-6xl mx-auto w-full px-6 pb-14">
          <Link to="/packages" data-testid="package-back-link" className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.18em] uppercase text-ivory/85 hover:text-sand-brand" style={{ transition: "color .2s ease" }}><ArrowLeft size={14} />{t("pkg.back")}</Link>
          <Reveal className="mt-6">
            <p className="text-xs font-medium tracking-super-wide text-sand-brand uppercase mb-4">{t("pkg.eyebrow")} · {t("pkg.days", { n: pkg.days })}</p>
            <h1 className="font-serif text-4xl md:text-6xl text-ivory font-normal leading-tight max-w-4xl" data-testid="package-title">{c.title}</h1>
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-ivory/85">
              <span className="inline-flex items-center gap-2"><CalendarRange size={15} className="text-sand-brand" />{t("pkg.days", { n: pkg.days })}</span>
              <span className="inline-flex items-center gap-2"><Users size={15} className="text-sand-brand" />{pkg.max_guests ? t("detail.upTo", { n: pkg.max_guests }) : t("type.group")}</span>
              <span className="inline-flex items-center gap-2" data-testid="package-hero-price">
                <span className="line-through text-ivory/60">{euro(pkg.full_price)}</span>
                <span className="font-serif text-2xl text-ivory">{euro(pkg.price)}</span> {t("detail.pp")}
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-6 py-16 md:py-24">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-7 space-y-12">
            <Reveal><p className="font-serif text-2xl md:text-3xl text-ocean leading-snug">{c.short}</p></Reveal>

            <Reveal className="flex items-start gap-4 bg-sage-mist/70 border border-sage-light/60 rounded-2xl p-5" data-testid="package-saving">
              <BadgePercent size={22} className="text-sage-dark shrink-0" />
              <div>
                <p className="text-sm font-semibold text-ocean">{t("pkg.save", { amount: euro(pkg.saving) })}</p>
                <p className="text-xs text-ocean/65 mt-1">{t("pkg.discountNote")}</p>
              </div>
            </Reveal>

            <Reveal>
              <p className="text-xs font-medium tracking-super-wide uppercase text-sea">{t("pkg.dayByDay")}</p>
              <div className="brand-line mt-3 mb-8" />
              <ol className="space-y-5" data-testid="package-itinerary">
                {pkg.tours.map((x, i) => (
                  <li key={x.slug}>
                    <Link to={`/tours/${x.slug}`} className="group flex gap-5 bg-white rounded-2xl p-4 border border-sand/70 shadow-soft hover:shadow-soft-lg" style={{ transition: "box-shadow .25s ease" }} data-testid={`package-day-${i + 1}`}>
                      <div className="img-zoom w-28 h-24 md:w-40 md:h-28 rounded-xl overflow-hidden shrink-0 bg-sand"><img src={x.image} alt={tourTitle(x, lang)} loading="lazy" className="w-full h-full object-cover" /></div>
                      <div className="min-w-0 flex-1 py-1">
                        <p className="text-[11px] font-medium tracking-[0.2em] uppercase text-sea">{t("pkg.day", { n: i + 1 })}</p>
                        <h3 className="font-serif text-lg md:text-xl text-ocean mt-1 leading-snug group-hover:text-sea" style={{ transition: "color .2s ease" }}>{tourTitle(x, lang)}</h3>
                        <p className="mt-2 flex items-center gap-4 text-xs text-ocean/55">
                          <span className="inline-flex items-center gap-1.5"><Clock size={13} />{t("card.hours", { n: x.duration_hours })}</span>
                          <span>{t("card.from")} {euro(x.price)}</span>
                        </p>
                      </div>
                      <ArrowUpRight size={17} className="text-ocean/40 group-hover:text-sea shrink-0 mt-1" />
                    </Link>
                  </li>
                ))}
              </ol>
              <p className="mt-5 text-xs text-ocean/55">{t("pkg.consecutive")}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28" id="book">
              <BookingWidget pkg={pkg} title={c.title} />
              <p className="mt-4 text-center text-xs text-ocean/50">{t("detail.reply")}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
